/**
 * Model provider adapters for the in-page "bring your own key" agent (SPEC §7).
 *
 * Two adapters share one `Provider` interface so the loop (`loop.ts`) never
 * knows which API it is talking to:
 *
 * - `openaiProvider`    → Chat Completions (`POST /v1/chat/completions`), tools
 *                          as function tools, results as `role:'tool'` messages.
 * - `anthropicProvider` → Messages API (`POST /v1/messages`) called straight
 *                          from the browser (`anthropic-dangerous-direct-browser-access`),
 *                          tools with `input_schema`, `tool_use` / `tool_result` blocks.
 *
 * Both take the same provider-neutral `ChatMessage[]` (a leading `system`
 * message is allowed and placed wherever the API wants it) and return a
 * `Completion` of text plus parsed tool calls. Tool-call arguments are decoded
 * defensively: malformed JSON never throws, it is reported on the call as
 * `argumentsError` so the loop can tell the model. HTTP and network failures
 * become `ProviderError`s with a readable, provider-specific message.
 *
 * The API key is only ever placed in the request header for the selected
 * provider's own origin; it is never logged.
 */

/** Which provider an adapter talks to. */
export type ProviderName = 'openai' | 'anthropic';

/** A JSON-schema object describing a tool's input (as registered with WebMCP). */
export type ToolInputSchema = Record<string, unknown>;

/** Provider-neutral description of a callable tool. */
export interface ToolSpec {
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
}

/** A tool call requested by the model, with arguments already decoded. */
export interface ToolCall {
  /** Provider-issued id, echoed back with the result. */
  id: string;
  name: string;
  /** Decoded arguments; `{}` when the model sent none or they were malformed. */
  arguments: Record<string, unknown>;
  /** The argument string exactly as the model sent it (OpenAI), for faithful echoing. */
  rawArguments?: string;
  /** Set when the arguments could not be decoded into a JSON object. */
  argumentsError?: string;
}

/** One turn of the provider-neutral conversation. */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  /** Text content; for `tool` messages, the tool result as text. */
  content: string;
  /** `assistant` only: tool calls the model requested in this turn. */
  toolCalls?: ToolCall[];
  /** `tool` only: the id of the call this message answers. */
  toolCallId?: string;
  /** `tool` only: the tool's name (informational). */
  toolName?: string;
}

/** What a provider returns for one model call. */
export interface Completion {
  text: string;
  toolCalls: ToolCall[];
}

/** Per-call options; `fetchImpl` exists so tests can assert the request without a network. */
export interface CompleteOptions {
  apiKey: string;
  model: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

/** One model provider. */
export interface Provider {
  name: ProviderName;
  /** Suggested model id; editable in the panel. */
  defaultModel: string;
  complete(messages: ChatMessage[], tools: ToolSpec[], opts: CompleteOptions): Promise<Completion>;
}

/** Why a provider call failed, for the panel to phrase advice. */
export type ProviderErrorKind = 'auth' | 'rate_limit' | 'network' | 'bad_response' | 'http' | 'aborted';

/** A readable, non-secret failure raised by a provider adapter. */
export class ProviderError extends Error {
  constructor(
    message: string,
    readonly kind: ProviderErrorKind,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ProviderError';
  }
}

export const OPENAI_DEFAULT_MODEL = 'gpt-5.4';
export const ANTHROPIC_DEFAULT_MODEL = 'claude-sonnet-5';
export const OPENAI_URL = 'https://api.openai.com/v1/chat/completions';
export const ANTHROPIC_URL = 'https://api.anthropic.com/v1/messages';
export const ANTHROPIC_VERSION = '2023-06-01';
export const ANTHROPIC_MAX_TOKENS = 2048;

const LABEL: Record<ProviderName, string> = { openai: 'OpenAI', anthropic: 'Anthropic' };

// ---------- shared helpers ----------

/** Decode a model-supplied argument payload into a plain object, never throwing. */
export function decodeArguments(raw: unknown): { arguments: Record<string, unknown>; rawArguments?: string; argumentsError?: string } {
  if (raw == null) return { arguments: {} };
  if (typeof raw === 'object') {
    if (Array.isArray(raw)) return { arguments: {}, argumentsError: 'arguments must be a JSON object, got an array' };
    return { arguments: raw as Record<string, unknown> };
  }
  if (typeof raw !== 'string') return { arguments: {}, argumentsError: `arguments must be a JSON object, got ${typeof raw}` };
  const text = raw.trim();
  if (text === '') return { arguments: {}, rawArguments: raw };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { arguments: {}, rawArguments: raw, argumentsError: `malformed JSON arguments: ${(e as Error).message}` };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { arguments: {}, rawArguments: raw, argumentsError: 'arguments must be a JSON object' };
  }
  return { arguments: parsed as Record<string, unknown>, rawArguments: raw };
}

function isAbort(e: unknown, signal?: AbortSignal): boolean {
  return signal?.aborted === true || (e as { name?: string } | null)?.name === 'AbortError';
}

/** Pull a human-readable message out of an error body (OpenAI and Anthropic both use `error.message`). */
function apiMessage(bodyText: string): string {
  try {
    const parsed = JSON.parse(bodyText) as { error?: { message?: unknown } | string; message?: unknown };
    const inner = typeof parsed.error === 'string' ? parsed.error : parsed.error?.message ?? parsed.message;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  } catch {
    /* not JSON */
  }
  const t = bodyText.replace(/\s+/g, ' ').trim();
  return t.length > 160 ? t.slice(0, 157) + '…' : t;
}

function httpError(provider: ProviderName, status: number, bodyText: string): ProviderError {
  const label = LABEL[provider];
  const detail = apiMessage(bodyText);
  const suffix = detail ? `: ${detail}` : '';
  if (status === 401 || status === 403) {
    return new ProviderError(`${label} rejected the API key (${status})${suffix}. Check the key and that it belongs to ${label}.`, 'auth', status);
  }
  if (status === 429) {
    return new ProviderError(`${label} rate limit (429)${suffix}. Wait a moment and try again.`, 'rate_limit', status);
  }
  if (status === 404 || status === 400) {
    return new ProviderError(`${label} refused the request (${status})${suffix}. Check the model name.`, 'http', status);
  }
  return new ProviderError(`${label} returned HTTP ${status}${suffix}.`, 'http', status);
}

/** POST JSON, turning transport/HTTP/parse failures into ProviderErrors. */
async function postJson(provider: ProviderName, url: string, headers: Record<string, string>, body: unknown, opts: CompleteOptions): Promise<unknown> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const label = LABEL[provider];
  const host = new URL(url).host;
  if (typeof fetchImpl !== 'function') throw new ProviderError('fetch is not available in this environment.', 'network');
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if (isAbort(e, opts.signal)) throw new ProviderError('Stopped.', 'aborted');
    const reason = e instanceof Error ? e.message : String(e);
    throw new ProviderError(`Could not reach ${host} (${reason}). Check your connection; ${label} must allow browser requests.`, 'network');
  }
  let text = '';
  try {
    text = await res.text();
  } catch (e) {
    if (isAbort(e, opts.signal)) throw new ProviderError('Stopped.', 'aborted');
    throw new ProviderError(`Could not read the ${label} response (${e instanceof Error ? e.message : String(e)}).`, 'network');
  }
  if (!res.ok) throw httpError(provider, res.status, text);
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ProviderError(`${label} returned something that is not JSON (HTTP ${res.status}).`, 'bad_response', res.status);
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

// ---------- OpenAI (Chat Completions) ----------

type OpenAIMessage =
  | { role: 'system' | 'user'; content: string }
  | { role: 'assistant'; content: string | null; tool_calls?: { id: string; type: 'function'; function: { name: string; arguments: string } }[] }
  | { role: 'tool'; tool_call_id: string; content: string };

function toOpenAIMessages(messages: ChatMessage[]): OpenAIMessage[] {
  const out: OpenAIMessage[] = [];
  for (const m of messages) {
    switch (m.role) {
      case 'system':
      case 'user':
        out.push({ role: m.role, content: m.content });
        break;
      case 'assistant': {
        const msg: OpenAIMessage = { role: 'assistant', content: m.content ? m.content : null };
        if (m.toolCalls?.length) {
          msg.tool_calls = m.toolCalls.map((c) => ({
            id: c.id,
            type: 'function',
            function: { name: c.name, arguments: c.rawArguments ?? JSON.stringify(c.arguments) },
          }));
        }
        out.push(msg);
        break;
      }
      case 'tool':
        out.push({ role: 'tool', tool_call_id: m.toolCallId ?? '', content: m.content });
        break;
    }
  }
  return out;
}

function parseOpenAI(data: unknown): Completion {
  const root = asRecord(data);
  const choice = asRecord((root?.choices as unknown[] | undefined)?.[0]);
  const message = asRecord(choice?.message);
  if (!message) throw new ProviderError('OpenAI returned no message in its response.', 'bad_response');
  const text = typeof message.content === 'string' ? message.content : '';
  const toolCalls: ToolCall[] = [];
  const rawCalls = Array.isArray(message.tool_calls) ? message.tool_calls : [];
  for (const [i, rc] of rawCalls.entries()) {
    const call = asRecord(rc);
    const fn = asRecord(call?.function);
    if (!call || !fn || typeof fn.name !== 'string') continue;
    toolCalls.push({ id: typeof call.id === 'string' ? call.id : `call_${i}`, name: fn.name, ...decodeArguments(fn.arguments) });
  }
  return { text, toolCalls };
}

/** OpenAI Chat Completions adapter. */
export const openaiProvider: Provider = {
  name: 'openai',
  defaultModel: OPENAI_DEFAULT_MODEL,
  async complete(messages, tools, opts) {
    const body: Record<string, unknown> = { model: opts.model, messages: toOpenAIMessages(messages) };
    if (tools.length) {
      body.tools = tools.map((t) => ({ type: 'function', function: { name: t.name, description: t.description, parameters: t.inputSchema } }));
      body.tool_choice = 'auto';
    }
    const data = await postJson('openai', OPENAI_URL, { Authorization: `Bearer ${opts.apiKey}` }, body, opts);
    return parseOpenAI(data);
  },
};

// ---------- Anthropic (Messages) ----------

type AnthropicBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: string };
type AnthropicMessage = { role: 'user' | 'assistant'; content: string | AnthropicBlock[] };

function toAnthropicMessages(messages: ChatMessage[]): { system?: string; messages: AnthropicMessage[] } {
  const systemParts: string[] = [];
  const out: AnthropicMessage[] = [];
  const last = () => out[out.length - 1];
  for (const m of messages) {
    if (m.role === 'system') {
      if (m.content) systemParts.push(m.content);
      continue;
    }
    if (m.role === 'user') {
      if (!m.content) continue;
      const prev = last();
      if (prev?.role === 'user') {
        // Two user turns in a row are not allowed; fold this one into the previous.
        const blocks: AnthropicBlock[] = typeof prev.content === 'string' ? [{ type: 'text', text: prev.content }] : prev.content;
        blocks.push({ type: 'text', text: m.content });
        prev.content = blocks;
      } else out.push({ role: 'user', content: m.content });
      continue;
    }
    if (m.role === 'assistant') {
      const blocks: AnthropicBlock[] = [];
      const text = m.content.trim();
      if (text) blocks.push({ type: 'text', text });
      for (const c of m.toolCalls ?? []) blocks.push({ type: 'tool_use', id: c.id, name: c.name, input: c.arguments });
      if (!blocks.length) continue;
      out.push({ role: 'assistant', content: blocks });
      continue;
    }
    // tool result → user turn; consecutive results share one turn.
    const block: AnthropicBlock = { type: 'tool_result', tool_use_id: m.toolCallId ?? '', content: m.content };
    const prev = last();
    if (prev?.role === 'user' && Array.isArray(prev.content) && prev.content.some((b) => b.type === 'tool_result')) {
      prev.content.push(block);
    } else out.push({ role: 'user', content: [block] });
  }
  const system = systemParts.length ? systemParts.join('\n\n') : undefined;
  return system === undefined ? { messages: out } : { system, messages: out };
}

function parseAnthropic(data: unknown): Completion {
  const root = asRecord(data);
  const blocks = Array.isArray(root?.content) ? root.content : null;
  if (!blocks) throw new ProviderError('Anthropic returned no content blocks in its response.', 'bad_response');
  const texts: string[] = [];
  const toolCalls: ToolCall[] = [];
  for (const [i, raw] of blocks.entries()) {
    const b = asRecord(raw);
    if (!b) continue;
    if (b.type === 'text' && typeof b.text === 'string') texts.push(b.text);
    else if (b.type === 'tool_use' && typeof b.name === 'string') {
      toolCalls.push({ id: typeof b.id === 'string' ? b.id : `toolu_${i}`, name: b.name, ...decodeArguments(b.input) });
    }
  }
  return { text: texts.join('').trim(), toolCalls };
}

/** Anthropic Messages adapter (direct browser access). */
export const anthropicProvider: Provider = {
  name: 'anthropic',
  defaultModel: ANTHROPIC_DEFAULT_MODEL,
  async complete(messages, tools, opts) {
    const { system, messages: msgs } = toAnthropicMessages(messages);
    const body: Record<string, unknown> = { model: opts.model, max_tokens: ANTHROPIC_MAX_TOKENS, messages: msgs };
    if (system !== undefined) body.system = system;
    if (tools.length) body.tools = tools.map((t) => ({ name: t.name, description: t.description, input_schema: t.inputSchema }));
    const data = await postJson(
      'anthropic',
      ANTHROPIC_URL,
      {
        'x-api-key': opts.apiKey,
        'anthropic-version': ANTHROPIC_VERSION,
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body,
      opts,
    );
    return parseAnthropic(data);
  },
};

/** All adapters by name. */
export const PROVIDERS: Readonly<Record<ProviderName, Provider>> = { openai: openaiProvider, anthropic: anthropicProvider };

/** Look up an adapter by name. */
export function getProvider(name: ProviderName): Provider {
  return PROVIDERS[name];
}

/** True for a valid provider name (used to validate stored settings). */
export function isProviderName(v: unknown): v is ProviderName {
  return v === 'openai' || v === 'anthropic';
}
